import React from 'react';
import { Badge } from 'primereact/badge';
import { Carousel } from 'primereact/carousel';

interface VenueData {
    id: number;
    name: string;
    address: string;
    subscription: string;
    staffToday: number;
    coversServed: number;
    revenue: string;
    customerRating: number;
}

interface VenueOverviewProps {
    venues: VenueData[];
}

const VenueOverview: React.FC<VenueOverviewProps> = ({ venues }) => {
    const getSubscriptionBadge = (subscription: string) => {
        const badgeClass = subscription === 'Premium' ? 'p-badge-info' : 'p-badge-secondary';
        return <Badge value={subscription} className={`ml-2 ${badgeClass}`} />;
    };

    const venueTemplate = (venue: VenueData) => {
        return (
            <div className="venue-card">
                <div className="venue-card-header d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h5 className="venue-name mb-1">{venue.name}</h5>
                        {getSubscriptionBadge(venue.subscription)}
                    </div>
                </div>

                <div className="venue-address mb-3">
                    <i className="ri-map-pin-line text-muted me-2"></i>
                    <span className="text-muted">{venue.address}</span>
                </div>

                <div className="venue-metrics">
                    <div className="metric-row d-flex align-items-center mb-2">
                        <i className="ri-team-line me-2"></i>
                        <span className="metric-label">Staff Today</span>
                        <span className="metric-value ms-auto fw-bold">{venue.staffToday}</span>
                    </div>

                    <div className="metric-row d-flex align-items-center mb-2">
                        <i className="ri-restaurant-line me-2"></i>
                        <span className="metric-label">Covers Served</span>
                        <span className="metric-value ms-auto fw-bold">{venue.coversServed}</span>
                    </div>

                    <div className="metric-row d-flex align-items-center mb-2">
                        <i className="ri-money-dollar-circle-line me-2"></i>
                        <span className="metric-label">Revenue</span>
                        <span className="metric-value ms-auto fw-bold">{venue.revenue}</span>
                    </div>

                    <div className="metric-row d-flex align-items-center">
                        <i className="ri-star-fill text-warning me-2"></i>
                        <span className="metric-label">Customer Rating</span>
                        <span className="metric-value ms-auto fw-bold">{venue.customerRating}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="dashboard-card dashboard-card-wrapper coming-soon-card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <i className="ri-building-line me-2"></i>
                    Venue Overview
                </h5>
                <div className="venue-status-indicators d-flex align-items-center">
                    <div className="status-item me-3">
                        <i className="ri-checkbox-circle-fill text-success me-1"></i>
                        <span className="text-muted">Active</span>
                        <span className="fw-bold ms-1">{venues.filter((v) => true).length}</span>
                    </div>
                    <div className="status-item me-3">
                        <i className="ri-time-line text-warning me-1"></i>
                        <span className="text-muted">Exploring</span>
                        <span className="fw-bold ms-1">0</span>
                    </div>
                    <div className="status-item">
                        <i className="ri-close-circle-line text-danger me-1"></i>
                        <span className="text-muted">Underperforming</span>
                        <span className="fw-bold ms-1">0</span>
                    </div>
                </div>
            </div>

            <div className="card-body">
                <Carousel
                    value={venues}
                    itemTemplate={venueTemplate}
                    numVisible={3}
                    numScroll={1}
                    responsiveOptions={[
                        {
                            breakpoint: '1024px',
                            numVisible: 2,
                            numScroll: 1,
                        },
                        {
                            breakpoint: '768px',
                            numVisible: 1,
                            numScroll: 1,
                        },
                    ]}
                    className="venue-carousel"
                />
            </div>
        </div>
    );
};

export default VenueOverview;
